import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Upload, Music, Check, Loader2, Search, ListMusic, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const AcceptSuggestionDialog = ({ 
  open, 
  onOpenChange, 
  suggestion, 
  playlistId,
  eventId,
  onSuccess 
}) => {
  const [uploadMethod, setUploadMethod] = useState('existing');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [existingTracks, setExistingTracks] = useState([]);
  const [filteredTracks, setFilteredTracks] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [loadingTracks, setLoadingTracks] = useState(false);

  const suggestedTitle = suggestion?.song_title || suggestion?.requested_title || '';
  const suggestedArtist = suggestion?.artist || suggestion?.requested_artist || '';

  useEffect(() => {
    if (open && eventId) {
      loadExistingTracks();
    }
  }, [open, eventId]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredTracks(existingTracks);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = existingTracks.filter(
        track => 
          track.title?.toLowerCase().includes(query) ||
          track.artist?.toLowerCase().includes(query)
      );
      setFilteredTracks(filtered);
    }
  }, [searchQuery, existingTracks]);

  useEffect(() => {
    if (open && suggestedTitle) {
      setSearchQuery(suggestedTitle);
    }
  }, [open, suggestedTitle]);

  const loadExistingTracks = async () => {
    setLoadingTracks(true);
    try {
      const response = await api.get(`/events/${eventId}/tracks`);
      setExistingTracks(response.data.tracks || []);
      setFilteredTracks(response.data.tracks || []);
    } catch (error) {
      console.error('Error loading tracks:', error);
      setExistingTracks([]);
    } finally {
      setLoadingTracks(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.type.startsWith('audio/')) {
        toast.error('Veuillez sélectionner un fichier audio');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleSelectExistingTrack = async (track) => {
    setSelectedTrack(track);
    setUploading(true);
    
    try {
      await api.patch(`/suggestions/${suggestion.id}/status`, null, {
        params: {
          status: 'accepted',
          linked_track_id: track.id
        }
      });
      
      toast.success(`✅ "${track.title}" lié à la suggestion !`);
      
      if (onSuccess) {
        onSuccess(track, suggestion.id, 'linked');
      }
      
      handleClose();
    } catch (error) {
      console.error('Error linking track:', error);
      toast.error('Erreur lors de la liaison de la piste');
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Veuillez sélectionner un fichier audio');
      return;
    }

    if (!playlistId) {
      toast.error('❌ Aucune partie active. Veuillez d\'abord activer une partie.');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('playlist_id', playlistId);
      formData.append('title', suggestedTitle || file.name.replace(/\.[^/.]+$/, ''));
      formData.append('artist', suggestedArtist || 'Unknown');
      formData.append('order_index', 999);

      const response = await api.post('/tracks/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      await api.patch(`/suggestions/${suggestion.id}/status`, null, {
        params: {
          status: 'accepted',
          linked_track_id: response.data.id
        }
      });

      toast.success(`🎵 "${suggestedTitle}" ajouté et lié à la suggestion !`);
      
      if (onSuccess) {
        onSuccess(response.data, suggestion.id, 'uploaded');
      }

      handleClose();
    } catch (error) {
      console.error('Upload error:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('❌ Session expirée. Veuillez vous reconnecter.');
      } else {
        toast.error(`❌ Erreur: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setSelectedTrack(null);
    setSearchQuery('');
    setUploadMethod('existing');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-3">
            <Music className="w-6 h-6 text-violet-600" />
            Accepter la suggestion
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4 flex-1 overflow-hidden flex flex-col">
          {/* Suggestion Info */}
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 flex-shrink-0">
            <h3 className="font-semibold text-violet-700 mb-2">Demande invité :</h3>
            <p className="text-lg font-bold text-gray-900">{suggestedTitle || 'Titre non spécifié'}</p>
            {suggestedArtist && <p className="text-sm text-gray-600">{suggestedArtist}</p>}
            {suggestion?.guest_name && (
              <p className="text-xs text-gray-500 mt-2">Par : {suggestion.guest_name}</p>
            )}
          </div>

          {/* Method Selection */}
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={() => setUploadMethod('existing')}
              className={`flex-1 p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                uploadMethod === 'existing'
                  ? 'border-violet-500 bg-violet-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <ListMusic className="w-5 h-5" />
              <span className="font-medium">Piste existante</span>
            </button>

            <button
              onClick={() => setUploadMethod('upload')}
              className={`flex-1 p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                uploadMethod === 'upload'
                  ? 'border-violet-500 bg-violet-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <Upload className="w-5 h-5" />
              <span className="font-medium">Charger fichier</span>
            </button>
          </div>

          {/* Existing Tracks Section */}
          {uploadMethod === 'existing' && (
            <div className="flex-1 flex flex-col min-h-0">
              <div className="relative flex-shrink-0 mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Rechercher une piste..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-gray-50 border-gray-200 text-gray-900"
                />
              </div>

              <div className="flex-1 overflow-y-auto space-y-2 min-h-[200px] max-h-[300px]">
                {loadingTracks ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
                    <span className="ml-2 text-gray-500">Chargement...</span>
                  </div>
                ) : filteredTracks.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {existingTracks.length === 0 ? (
                      <p>Aucune piste dans cet événement. Chargez un fichier.</p>
                    ) : (
                      <p>Aucune piste trouvée pour "{searchQuery}"</p>
                    )}
                  </div>
                ) : (
                  filteredTracks.map((track) => (
                    <button
                      key={track.id}
                      onClick={() => handleSelectExistingTrack(track)}
                      disabled={uploading}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                        selectedTrack?.id === track.id
                          ? 'border-violet-500 bg-violet-50'
                          : 'border-gray-200 bg-white hover:border-violet-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-violet-100 to-purple-100 rounded-lg flex items-center justify-center">
                        <Music className="w-5 h-5 text-violet-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-900 font-medium truncate">{track.title}</p>
                        <p className="text-gray-500 text-sm truncate">
                          {track.artist || 'Artiste inconnu'}
                          {track.playlist_name && (
                            <span className="text-gray-400"> • {track.playlist_name}</span>
                          )}
                        </p>
                      </div>
                      {track.duration && (
                        <div className="flex items-center gap-1 text-gray-400 text-sm flex-shrink-0">
                          <Clock className="w-3 h-3" />
                          {formatDuration(track.duration)}
                        </div>
                      )}
                      {selectedTrack?.id === track.id && (
                        <Check className="w-5 h-5 text-violet-600 flex-shrink-0" />
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Upload Section */}
          {uploadMethod === 'upload' && (
            <div className="space-y-4 flex-1">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-violet-400 transition-colors">
                <input
                  type="file"
                  id="track-upload"
                  accept="audio/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label htmlFor="track-upload" className="cursor-pointer flex flex-col items-center">
                  <Upload className="w-12 h-12 text-violet-500 mb-3" />
                  {file ? (
                    <div className="text-center">
                      <p className="text-gray-900 font-semibold">{file.name}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.preventDefault(); setFile(null); }}
                        className="mt-2 text-violet-600"
                      >
                        Changer de fichier
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="text-gray-900 font-semibold mb-1">
                        Cliquez pour sélectionner un fichier
                      </p>
                      <p className="text-sm text-gray-500">MP3, WAV, FLAC, OGG</p>
                    </div>
                  )}
                </label>
              </div>

              {file && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-3">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-emerald-700">
                      Prêt : <span className="font-semibold">{suggestedTitle || file.name}</span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="mt-4 flex-shrink-0">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={uploading}
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Annuler
          </Button>
          {uploadMethod === 'upload' && (
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Upload...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Charger et Accepter
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AcceptSuggestionDialog;
